import React from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  imageUrl?: string;
  keywords?: string;
  path?: string;
  type?: 'website' | 'article' | 'profile';
  structuredData?: any;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl = 'https://www.tlcmauritania.org',
  imageUrl = '/images/tlc-social-share.jpg',
  keywords = 'TLC, International School, Mauritania, Education, Nouakchott, School',
  path = '',
  type = 'website',
  structuredData,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.language.split('-')[0] as string;
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const fullUrl = `${canonicalUrl}${path}`;
  const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${canonicalUrl}${imageUrl}`;
  
  // Generate alternate language URLs
  const alternateUrls = {
    en: `${canonicalUrl}/en${path}`,
    fr: `${canonicalUrl}/fr${path}`,
    ar: `${canonicalUrl}/ar${path}`,
  };

  // Default structured data for TLC School
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "TLC International School",
    "alternateName": "TLC School",
    "description": "A global learning community in the heart of Nouakchott, Mauritania providing American-accredited education from preschool to grade 12.",
    "url": canonicalUrl,
    "logo": `${canonicalUrl}/images/logos/tlc-logo-removebg-preview.png`,
    "image": fullImageUrl,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Nouakchott",
      "addressLocality": "Nouakchott",
      "addressCountry": "MR"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+222-45-25-16-85",
      "contactType": "admissions",
      "availableLanguage": ["English", "French", "Arabic"]
    },
    "sameAs": [
      "https://www.facebook.com/tlcmauritania",
      "https://www.instagram.com/tlcmauritania"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} dir={dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="TLC International School" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="TLC International School" />
      <meta property="og:locale" content={language === 'ar' ? 'ar_MR' : language === 'fr' ? 'fr_MR' : 'en_US'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@tlcmauritania" />
      <meta name="twitter:creator" content="@tlcmauritania" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      
      {/* Hreflang Tags */}
      <link rel="alternate" hrefLang="en" href={alternateUrls.en} />
      <link rel="alternate" hrefLang="fr" href={alternateUrls.fr} />
      <link rel="alternate" hrefLang="ar" href={alternateUrls.ar} />
      <link rel="alternate" hrefLang="x-default" href={alternateUrls.en} />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
    </Helmet>
  );
};

export default SEO; 